// Shared admin chrome for Circles, Networks, and Spaces.
//
// The outer component dispatches on `kind` to a kind-specific inner that
// runs its own detail + join-requests fetches and projects them onto the
// uniform `{name, archived, pendingCount}` shape the shared body consumes.
// The shared body owns the sidebar config and breadcrumb.
//
// While the detail query is in flight, the breadcrumb renders a Skeleton
// so the chrome paints immediately and the user can navigate sections
// without a blank page.

import type { ReactNode } from 'react';
import { Skeleton, type SidebarItem } from '@leanin/ui';
import { useTranslations } from '@/i18n/translations';
import { useQuery } from '@tanstack/react-query';
import { groupKeys, useIntakeForm } from '@/lib/queries/groups';
import { spacesKeys } from '@/lib/queries/spaces';
import { AdminShell } from '../../admin/AdminShell';
import { useReviewableJoinRequests } from './use-reviewable-join-requests';
import {
  SECTIONS_BY_KIND,
  type GroupAdminKind,
  type GroupAdminSection,
} from './sections';

export type { GroupAdminKind, GroupAdminSection } from './sections';

type SidebarSectionId = Exclude<GroupAdminSection, 'overview'>;

type Translator = ReturnType<typeof useTranslations<'Group'>>;

type State = {
  readonly name: string | null;
  readonly archived: boolean;
  readonly pendingCount: number;
};

const KIND_CONFIG: Record<
  GroupAdminKind,
  {
    readonly back: (id: string) => string;
    readonly base: (id: string) => string;
  }
> = {
  circle: {
    back: (id) => `/circles/${id}`,
    base: (id) => `/circles/${id}/admin`,
  },
  network: {
    back: (id) => `/networks/${id}`,
    base: (id) => `/networks/${id}/admin`,
  },
  space: {
    back: (id) => `/groups/${id}`,
    base: (id) => `/groups/${id}/admin`,
  },
};

// Sidebar items per kind = the full section set minus 'overview' (which
// renders as the implicit landing item).
const SIDEBAR_SECTIONS: Record<GroupAdminKind, ReadonlyArray<SidebarSectionId>> = {
  circle: SECTIONS_BY_KIND.circle.filter((s) => s !== 'overview') as ReadonlyArray<SidebarSectionId>,
  network: SECTIONS_BY_KIND.network.filter((s) => s !== 'overview') as ReadonlyArray<SidebarSectionId>,
  space: SECTIONS_BY_KIND.space.filter((s) => s !== 'overview') as ReadonlyArray<SidebarSectionId>,
};

const SIDEBAR_ITEM_DEFS: Record<
  SidebarSectionId,
  (t: Translator, base: string, archived: boolean, pendingCount: number) => SidebarItem
> = {
  visibility: (t, base) => ({ id: 'visibility', label: t('admin.shell.visibility'), icon: 'lock', href: `${base}?section=visibility` }),
  'join-questions': (t, base) => ({ id: 'join-questions', label: t('admin.shell.joinQuestions'), icon: 'pencil', href: `${base}?section=join-questions` }),
  requests: (t, base, _archived, pendingCount) => ({
    id: 'requests', label: t('admin.shell.requests'), icon: 'users', href: `${base}?section=requests`,
    ...(pendingCount > 0 ? { badge: pendingCount } : {}),
  }),
  members: (t, base) => ({ id: 'members', label: t('admin.shell.members'), icon: 'users', href: `${base}?section=members` }),
  circles: (t, base) => ({ id: 'circles', label: t('admin.shell.circles'), icon: 'circles', href: `${base}?section=circles` }),
  broadcasts: (t, base) => ({ id: 'broadcasts', label: t('admin.shell.broadcasts'), icon: 'email', href: `${base}?section=broadcasts` }),
  archive: (t, base, archived) => ({
    id: 'archive', label: archived ? t('admin.shell.restore') : t('admin.shell.archive'),
    icon: archived ? 'spark' : 'trash', href: `${base}?section=archive`,
  }),
};

type Props = {
  readonly id: string;
  readonly kind: GroupAdminKind;
  readonly section: GroupAdminSection;
  readonly children: ReactNode;
};

export function GroupAdminShell({ id, kind, section, children }: Props) {
  if (kind === 'circle') return <CircleInner id={id} section={section}>{children}</CircleInner>;
  if (kind === 'network') return <NetworkInner id={id} section={section}>{children}</NetworkInner>;
  return <SpaceInner id={id} section={section}>{children}</SpaceInner>;
}

type InnerProps = Omit<Props, 'kind'>;

function CircleInner({ id, section, children }: InnerProps) {
  const detail = useQuery(groupKeys.circle.detail(id)).data;
  const { requests } = useReviewableJoinRequests('circle', id);
  const circle = detail?.access === 'visitor' ? null : detail?.circle ?? null;
  // D18: the "Join questions" entry is shown only for a request-to-join group
  // whose intake feature is available. Policy is known client-side (constraint
  // #2 — don't probe unless it's request-to-join); availability is learned from
  // the server via the reused intake-form config query (200 = available incl.
  // no-form-yet, 404 = flag off). No client-side flag read (D4).
  const policyIsRequest = circle?.joinPolicy === 'request';
  const intakeProbe = useIntakeForm(id, 'member_to_circle', { enabled: policyIsRequest });
  return (
    <Body kind="circle" id={id} section={section} showJoinQuestions={policyIsRequest && intakeProbe.isSuccess} state={{
      name: circle?.name ?? null,
      archived: circle?.status === 'archived',
      pendingCount: requests.length,
    }}>{children}</Body>
  );
}

function NetworkInner({ id, section, children }: InnerProps) {
  const network = useQuery(groupKeys.network.detail(id)).data;
  const { requests } = useReviewableJoinRequests('network', id);
  // Networks are always request-to-join (JOIN_LOCKED), so the policy precondition
  // (constraint #2) holds once we know the network loaded; probe for availability.
  const intakeProbe = useIntakeForm(id, 'member_to_network', { enabled: network !== undefined });
  return (
    <Body kind="network" id={id} section={section} showJoinQuestions={intakeProbe.isSuccess} state={{
      name: network?.name ?? null,
      archived: network?.status === 'archived',
      pendingCount: requests.length,
    }}>{children}</Body>
  );
}

function SpaceInner({ id, section, children }: InnerProps) {
  const space = useQuery(spacesKeys.detail(id)).data;
  // Spaces have no join-requests or join-questions admin section.
  return (
    <Body kind="space" id={id} section={section} state={{
      name: space?.name ?? null, archived: space?.archivedAt != null, pendingCount: 0,
    }}>{children}</Body>
  );
}

function Body({
  kind, id, section, state, showJoinQuestions = false, children,
}: {
  readonly kind: GroupAdminKind;
  readonly id: string;
  readonly section: GroupAdminSection;
  readonly state: State;
  // D18 rollout plumbing: gate the "Join questions" entry on the server-driven
  // availability probe. Remove with the `groups.intake_form` flag at GA — the
  // entry then shows whenever it's in `SIDEBAR_SECTIONS` (i.e. by authorization
  // + request-to-join policy) with no probe. Other sections are unaffected.
  readonly showJoinQuestions?: boolean;
  readonly children: ReactNode;
}) {
  const t = useTranslations('Group');
  const config = KIND_CONFIG[kind];
  const base = config.base(id);
  const sidebarSections = SIDEBAR_SECTIONS[kind].filter(
    (s) => s !== 'join-questions' || showJoinQuestions,
  );
  const items: SidebarItem[] = [
    { id: 'overview', label: t('admin.shell.overview', { kind }), icon: 'spark', href: base },
    ...sidebarSections.map((s) => SIDEBAR_ITEM_DEFS[s](t, base, state.archived, state.pendingCount)),
  ];
  const groupName: ReactNode = state.name !== null ? state.name : <Skeleton width={140} height={14} rounded="sm" />;
  return (
    <AdminShell backHref={config.back(id)} backLabel={groupName} items={items} active={section}>
      {children}
    </AdminShell>
  );
}
